import React, { useRef, useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import { Button, Chip, DateIntervalField, Grid, MenuItem, SelectField, Box, Typography, Stack, Icons, DateTimeStandard } from "@curves/ui-base";
import useClubPurchaseReceiptApi from "@apis/useClubPurchaseReceiptApi";
import { displayDateText, getCurrentEndDay, getCurrentStartDate, parseDate, getToday, getSubMonths } from "@util/moment";
import { CLUB_INVENTORY_ROUTER } from "@clubInventory/router/routerPath";
import { callRef, ensureArray, isEqual, isFunction, refIsRequiredError } from "@util/utils";
import useOverview from "@util/hook/useOverview";
import useOptions from "@util/hook/useOptions";
import { Overview } from "@curves/ui-shared";
import { InspectionProgress } from "@clubInventory/components";
import PurchaseDialogBox from "@clubInventory/purchase/dialog/PurchaseDialogBox";
import { CLUB_PURCHASE_RECEIPT_STATUS } from "@clubInventory/constants/dataEnums";
import { statusColorByValue } from "@clubInventory/purchase/PurchaseOverviewPage.config";

// 功能：預設排序欄位代碼
const defaultSortBy = 2;

// 功能：預設每頁筆數
const defaultPageSize = 10;

// 功能：進貨單總覽表格欄位定義
const headerRow = [
	{
		cellKey: "clubPurchaseReceiptID",
		headerLabel: "進貨單號",
		align: "center",
		formatCell: (clubPurchaseReceiptID, { purchaseNo, purchaseStatusText, purchaseStatus }) => (
			<Stack direction="column" alignItems="center" justifyContent="center">
				<Button component={Link} to={CLUB_INVENTORY_ROUTER.PURCHASE.GET_ORDER_PATH(clubPurchaseReceiptID)}>
					{purchaseNo}
				</Button>
				<Chip label={purchaseStatusText} color={statusColorByValue[purchaseStatus] || "info"} />
			</Stack>
		),
	},
	{
		cellKey: "sourceTypeText",
		headerLabel: "來源",
		formatCell: (sourceTypeText, { sourceClubName }) =>
			sourceClubName ? (
				<Stack direction="column">
					<Typography variant="body1" className="mb-1">
						{sourceClubName}
					</Typography>
					<Typography variant="body2" className="font-color-3 font-size-small mb-0">
						{sourceTypeText}
					</Typography>
				</Stack>
			) : (
				<Typography variant="body1" className="mb-0">
					{sourceTypeText}
				</Typography>
			),
	},
	{
		cellKey: "purchaseDate",
		headerLabel: "建單時間",
		formatCell: value => parseDate(value, DateTimeStandard.DisplayDate),
	},
	{
		cellKey: "inspectionDeadline",
		headerLabel: "點收截止日",
		tip: "若超過點收截止日，系統將自動完成入庫",
		formatCell: value => parseDate(value, DateTimeStandard.DisplayDate),
	},
	{
		cellKey: "actualQuantity",
		headerLabel: "驗收進度",
		minWidth: 200,
		formatCell: (actualQuantity, { quantity, purchaseStatus }) => {
			const isExceptionClosed = purchaseStatus === CLUB_PURCHASE_RECEIPT_STATUS.EXCEPTIONCLOSED;
			return <InspectionProgress numerator={actualQuantity} denominator={quantity} status={isExceptionClosed ? "error" : actualQuantity === quantity ? "success" : "primary"} />;
		},
	},
	{
		cellKey: "purchaseReceiptActions",
		align: "center",
		headerLabel: "",
		formatCell: (_, { purchaseStatus, clubPurchaseReceiptID }, { openPurchaseInspectionAndStorage }) => {
			if (!openPurchaseInspectionAndStorage) return null;
			const isPending = isEqual(purchaseStatus, CLUB_PURCHASE_RECEIPT_STATUS.WAITTINSPECTION); // 待驗收
			const isInProgress = isEqual(purchaseStatus, CLUB_PURCHASE_RECEIPT_STATUS.PARTIALINSPECTION); // 驗收中
			if (!isPending && !isInProgress) return null;

			return (
				<Stack direction="row" spacing={2}>
					<Button variant="contained" color={isPending ? "secondary" : "primary"} startIcon={<Icons.CarrierCode />} onClick={() => openPurchaseInspectionAndStorage({ clubPurchaseReceiptID })}>
						{isPending ? "開始驗收" : "繼續驗收"}
					</Button>
				</Stack>
			);
		},
	},
];

// 功能：進貨管理總覽（搜尋、列表、開啟新增/驗收 Dialog）
const PurchaseOverviewPage = () => {
	const { postClubPurchaseReceiptOverviewApi, getClubPurchaseReceiptOptionsApi } = useClubPurchaseReceiptApi();
	const { options } = useOptions({
		getOptionsApi: getClubPurchaseReceiptOptionsApi,
		isFetchOnMount: true,
	});
	const today = getToday();
	const { overviewData, searchParams, searchParamsVersion, getOverviewData, updateOverviewPage, updateOverviewSort } = useOverview({
		getOverviewApi: postClubPurchaseReceiptOverviewApi,
		defaultSearchParams: {
			pageIndex: 1,
			pageSize: defaultPageSize,
			sortBy: defaultSortBy,
			startDate: getCurrentStartDate(getSubMonths(today, 3)),
			endDate: getCurrentEndDay(today),
			purchaseStatus: 99,
			sourceType: 99,
		},
		isFetchOnMount: true,
	});

	// 功能：搜尋表單與彈窗 ref（供 getResult / 開啟 Dialog 使用）
	const dateRef = useRef(null);
	const purchaseStatusRef = useRef(null);
	const sourceTypeRef = useRef(null);
	const dialogBoxRef = useRef(null);

	// 功能：組合目前篩選條件為查詢參數
	const getParamsData = useCallback(
		() => {
			let result = {
				pageIndex: 1,
				pageSize: defaultPageSize,
				sortBy: defaultSortBy,
			};

			const dateRange = callRef(dateRef, "getResult");
			if (dateRange) {
				const { startDate, endDate } = dateRange;
				result.startDate = getCurrentStartDate(startDate);
				result.endDate = getCurrentEndDay(endDate);
			}

			const purchaseStatus = callRef(purchaseStatusRef, "getResult");
			if (purchaseStatus !== undefined && purchaseStatus !== null) result.purchaseStatus = purchaseStatus;

			const sourceType = callRef(sourceTypeRef, "getResult");
			if (sourceType !== undefined && sourceType !== null) result.sourceType = sourceType;

			return result;
		},

		// eslint-disable-next-line
		[searchParams],
	);

	// 功能：搜尋送出（驗證必填後回傳查詢參數）
	const handleSearchSubmit = () => {
		const isError = refIsRequiredError(dateRef, purchaseStatusRef, sourceTypeRef);

		if (!isError) {
			const paramsData = getParamsData();
			console.log(paramsData);
			return { filters: paramsData, syncQuery: true };
		}
		return false;
	};

	// 功能：開啟新增進貨 Dialog（成功後刷新列表）
	const handleOpenCreatePurchaseDialog = () => {
		const { openCreatePurchaseDialog } = dialogBoxRef.current ?? {};
		if (isFunction(openCreatePurchaseDialog)) {
			openCreatePurchaseDialog(null, () => {
				getOverviewData(getParamsData());
			});
		}
	};

	// 功能：開啟驗收/入庫 Dialog（成功後刷新列表）
	const handleOpenPurchaseInspectionAndStorageDialog = useCallback(
		row => {
			const { openPurchaseInspectionAndStorageDialog } = dialogBoxRef.current ?? {};
			if (isFunction(openPurchaseInspectionAndStorageDialog)) {
				const payload = { purchaseReceiptID: row?.purchaseReceiptID, ...row };
				openPurchaseInspectionAndStorageDialog(payload, () => {
					getOverviewData(getParamsData());
				});
			}
		},
		[getOverviewData, getParamsData],
	);

	// 功能：Overview context（列表資料/分頁/排序/重新查詢）
	const contextValue = useMemo(
		() => ({
			overviewData,
			searchParams,
			getOverviewData,
			updateOverviewPage,
			updateOverviewSort,
		}),
		[overviewData, searchParams, getOverviewData, updateOverviewPage, updateOverviewSort],
	);

	return (
		<Box className="px-3 py-2">
			<Stack justifyContent="space-between" alignItems="center" className="mb-2">
				<Typography variant="h5" className="mb-0 font-weight-bold">
					進貨單總覽
				</Typography>
				<Button variant="outlined" onClick={handleOpenCreatePurchaseDialog}>
					新增進貨
				</Button>
			</Stack>
			<Overview contextValue={contextValue} idKey="purchaseReceiptID">
				{/* 搜尋區 */}
				<Overview.Search className="p-3 mb-3" getSearchParamsOnSubmit={handleSearchSubmit}>
					<Grid container spacing={2}>
						<Grid xs={6} item>
							<DateIntervalField
								label={"建單區間"}
								key={`${displayDateText(searchParams?.startDate, searchParams?.endDate)}-${searchParamsVersion}`}
								ref={dateRef}
								startDateProps={{
									defaultValue: searchParams?.startDate || "",
								}}
								endDateProps={{
									defaultValue: searchParams?.endDate || "",
								}}
								fullWidth
							/>
						</Grid>
						<Grid xs={3} item>
							<SelectField
								label={"狀態"}
								key={`${searchParams?.purchaseStatus}-${searchParamsVersion}`}
								ref={purchaseStatusRef}
								defaultValue={searchParams?.purchaseStatus}
								fullWidth
								required
							>
								{ensureArray(options?.clubPurchaseReceiptStatusOptions).map(({ text, value }) => {
									return (
										<MenuItem key={value} value={value}>
											{text}
										</MenuItem>
									);
								})}
							</SelectField>
						</Grid>
						<Grid xs={3} item>
							<SelectField
								label={"貨源"}
								key={`${searchParams?.sourceType}-${searchParamsVersion}`}
								ref={sourceTypeRef}
								defaultValue={searchParams?.sourceType}
								fullWidth
								required
							>
								{ensureArray(options?.clubSourceTypeOptions).map(({ text, value }) => {
									return (
										<MenuItem key={value} value={value}>
											{text}
										</MenuItem>
									);
								})}
							</SelectField>
						</Grid>
					</Grid>
					<Box className="mt-3 text-right">
						<Button variant="contained" type="submit">
							搜尋
						</Button>
					</Box>
				</Overview.Search>

				{/* 清單資訊 */}
				<Stack direction="row" justifyContent="end" alignItems="center" spacing={2} className="mb-2">
					<Overview.TotalCount className="mb-0" showCurrentCount />
					<SelectField key={searchParams?.sortBy} defaultValue={searchParams?.sortBy} onChange={e => updateOverviewSort(e.target.value)}>
						{ensureArray(options?.purchaseReceiptSortByOption).map(({ text, value }) => (
							<MenuItem key={value} value={value}>
								{text}
							</MenuItem>
						))}
					</SelectField>
				</Stack>

				{/* 列表區 */}
				<Overview.Table className={"mb-3"} headerRow={headerRow} cellRenderContext={{ openPurchaseInspectionAndStorage: handleOpenPurchaseInspectionAndStorageDialog }} />

				{/* 分頁 */}
				<Overview.Pagination />
			</Overview>
			<PurchaseDialogBox ref={dialogBoxRef} />
		</Box>
	);
};

export default PurchaseOverviewPage;
